import { Box } from "@mui/joy";
import React, { useState, useRef, useEffect } from 'react';
import { MediaControlBar, MediaTimeDisplay, MediaTimeRange, MediaController, MediaPlayButton } from "media-chrome/react";
import ReactPlayer from "react-player";
import S3_URL from "utils/s3";
import MuteButton from "./MuteButton";
import Poster from "./Poster";

interface GeekPlayerProps {
  videoId?: string;
  localVideoUrl?: string;
  playing?: boolean;
  aspectRatio?: string;
  globalMuted?: boolean;
  setGlobalMuted?: Function;
  setGlobalPlaying?: Function;
  showMuteButton?: boolean;
  showScrubber?: boolean;
}

const GeekPlayer: React.FC<GeekPlayerProps> = ({
  videoId,
  localVideoUrl,
  playing = true,
  aspectRatio = "3/4",
  globalMuted = undefined,
  setGlobalMuted = undefined,
  setGlobalPlaying = undefined,
  showMuteButton = true,
  showScrubber = true,
}) => {
  const [internalPlaying, setInternalPlaying] = useState(playing);
  const [localMuted, setLocalMuted] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const playerRef = useRef<HTMLVideoElement>(null);
  const muted = globalMuted !== undefined ? globalMuted : localMuted;
  const isControlled = setGlobalPlaying !== undefined;
  const currentPlaying = isControlled ? playing : internalPlaying;
  
  // Use local video URL if provided, otherwise use S3 URL
  const src = localVideoUrl || (videoId ? `${S3_URL}processed/${videoId}/hls/${videoId}.m3u8` : '');
  const poster = videoId ? `${S3_URL}processed/${videoId}/frames/${videoId}-.0000000.jpg` : undefined;

  const handleClick = () => {
    if (showPoster) { 
      setShowPoster(false); 
      // If autoplay was blocked, start playing immediately
      if (autoplayBlocked) {
        if (isControlled) {
          setGlobalPlaying(true);
        } else {
          setInternalPlaying(true);
        }
      }
    } else {
      if (isControlled) {
        setGlobalPlaying(!currentPlaying);
      } else {
        setInternalPlaying(!currentPlaying);
      }
    }
  }

  const handleMute = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setGlobalMuted ? setGlobalMuted(!muted) : setLocalMuted(!muted);
  }

  const handleReady = () => {
    setTimeout(() => {
      if (playerRef.current && currentPlaying) {
        const videoElement = playerRef.current;
        setAutoplayBlocked(videoElement && videoElement.paused);
        setShowPoster(videoElement && videoElement.paused);
      }
    }, 100);
  }

  // Comprehensive cleanup on unmount and src change
  useEffect(() => {
    // Pause any previous video when src changes
    if (playerRef.current) {
      const videoElement = playerRef.current;
      videoElement.pause();
    }

    // Cleanup function - runs on unmount or before next effect
    return () => {
      if (playerRef.current) {
        const videoElement = playerRef.current;
        try {
          videoElement.pause();
          videoElement.removeAttribute('src');
          videoElement.load();
          // Clear any event listeners
          videoElement.onloadeddata = null;
          videoElement.onplay = null;
          videoElement.onpause = null;
        } catch (e) {
          // Ignore errors if element is already cleaned up
          console.debug('Video cleanup error (safe to ignore):', e);
        }
      }
    };
  }, [src]);

  // // Autoplay debug statements - uncomment to see what's going on
  // console.log("playing", currentPlaying);
  // console.log("showPoster", showPoster);
  // console.log("autoplayBlocked", autoplayBlocked);

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        position: "relative",
        flexGrow: 1,
        width: "100%",
        height: "100%",
        borderRadius: "16px",
        overflow: "hidden",
        "& media-play-button": {
          borderTopLeftRadius: "50%",
          borderBottomLeftRadius: "50%",
        },
        "& media-time-display": {
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
        }
      }}
    >
      <MediaController style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "12px",
      }}>
        {src && (
          <ReactPlayer
            ref={playerRef}
            src={src}
            slot="media"
            playing={currentPlaying}
            muted={muted}
            light={showPoster && poster ? <Poster posterUrl={poster} /> : false}
            playsInline={true}
            autoPlay={currentPlaying}
            preload="metadata"
            loop={true}
            controls={false}
            height={"100%"}
            width={"auto"}
            style={{
              position: "absolute",
              textAlign: "left",
              aspectRatio: aspectRatio,
            }}
            onReady={handleReady}
            onPlay={() => setShowPoster(false)}
            onPause={() => {
              if (!autoplayBlocked) { setShowPoster(false); }
            }}
          />
        )}
        {showMuteButton && !showPoster && (
          <MuteButton muted={muted} handleMute={handleMute} />
        )}
        {showScrubber && (
        <MediaControlBar style={{ padding: "10px" }}>
          <MediaPlayButton />
          <MediaTimeRange />
          <MediaTimeDisplay />
        </MediaControlBar>
        )}
      </MediaController>
    </Box>
  )
}

export default GeekPlayer;